import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import {
  DocumentsPurpose,
  ISosState,
  ISosTempDocuments,
  ISosTempPhoto,
  ISosVictimStatus,
  PhotoPurpose,
} from '../types';
import { IPolicyVehicles } from '../../interfaces/cabinet';

const initialState: ISosState = {
  fioOrIdn: '',
  phoneNumber: '',
  regionId: '',
  date: null,
  address: '',
  dscr: '',
  photo: [],
  photoTemp: [],
  locaton: '',
  docNumber: '',
  driver: '',
  vehicle: '',
  mark: '',
  model: '',
  docs: [],
  docsTemp: [],
  iin_victim: '',
  fullName_victim: '',
  status_victim: '',
  phone_victim: '',
  type_object: 'vehicle',
  iin_object: '',
  fullName_object: '',
  number_object: '',
  inspection_date: null,
  inspection_region: '',
  inspection_address: '',
  inspectionTab: 0,
  isVictim: false,
  evaluation_report_number: '',
  vehicles: [],
  perpetrator: {
    iin: '',
    full_name: '',
  },
  otp_code: '',
  refId: '',
  assessment_otp_code: '',
  assessment_refId: '',
  applicantAddress: '',
};

const sosSlice = createSlice({
  name: 'sosSlice',
  initialState,
  reducers: {
    reset: () => initialState,

    addDocNumber: (
      state: Draft<ISosState>,
      action: PayloadAction<{
        docNumber: string;
        perpetrator: {
          iin: string;
          full_name: string;
        };
      }>
    ) => {
      state.docNumber = action.payload.docNumber;
      state.perpetrator.iin = action.payload.perpetrator.iin;
      state.perpetrator.full_name = action.payload.perpetrator.full_name;
    },
    addVehicles: (
      state: Draft<ISosState>,
      action: PayloadAction<IPolicyVehicles[]>
    ) => {
      state.vehicles = action.payload;
    },

    addPhoto: (
      state: Draft<ISosState>,
      action: PayloadAction<{ url: string; purpose: PhotoPurpose }>
    ) => {
      state.photo.push(action.payload);
    },

    resetPhoto: (state: Draft<ISosState>) => {
      state.photo = [];
    },

    addPhotoTemp: (
      state: Draft<ISosState>,
      action: PayloadAction<ISosTempPhoto[]>
    ) => {
      state.photoTemp = action.payload;
    },

    addDocuments: (
      state: Draft<ISosState>,
      action: PayloadAction<{ url: string; purpose: DocumentsPurpose }>
    ) => {
      state.docs.push(action.payload);
    },

    resetDocuments: (state: Draft<ISosState>) => {
      state.docs = [];
    },

    addDocumentsTemp: (
      state: Draft<ISosState>,
      action: PayloadAction<ISosTempDocuments[]>
    ) => {
      state.docsTemp = action.payload;
    },

    addLocation: (state: Draft<ISosState>, action: PayloadAction<string>) => {
      state.locaton = action.payload;
    },

    addDate: (
      state: Draft<ISosState>,
      action: PayloadAction<Date | string>
    ) => {
      state.date = action.payload;
    },

    addInfo: (
      state: Draft<ISosState>,
      action: PayloadAction<{
        regionId: string;
        docNumber: string;
        date: Date;
        dscr: string;
        address: string;
        applicantAddress: string;
      }>
    ) => {
      state.regionId = action.payload.regionId;
      state.docNumber = action.payload.docNumber;
      state.date = action.payload.date;
      state.dscr = action.payload.dscr;
      state.address = action.payload.address;
      state.applicantAddress = action.payload.applicantAddress;
    },

    addCar: (
      state: Draft<ISosState>,
      action: PayloadAction<{ vehicle: string; mark: string; model: string }>
    ) => {
      state.vehicle = action.payload.vehicle;
      state.mark = action.payload.mark;
      state.model = action.payload.model;
    },

    addInjured: (
      state: Draft<ISosState>,
      action: PayloadAction<{
        iin_victim: string;
        fullName_victim: string;
        status_victim: ISosVictimStatus;
        phone_victim: string;
        iin_object: string;
        fullName_object: string;
        number_object: string;
      }>
    ) => {
      state.iin_victim = action.payload.iin_victim;
      state.fullName_victim = action.payload.fullName_victim;
      state.status_victim = action.payload.status_victim;
      state.phone_victim = action.payload.phone_victim;
      state.iin_object = action.payload.iin_object;
      state.fullName_object = action.payload.fullName_object;
      state.number_object = action.payload.number_object;
    },

    addInspection: (
      state: Draft<ISosState>,
      action: PayloadAction<{
        inspection_date: Date | string;
        inspection_region: string;
        inspection_address: string;
      }>
    ) => {
      state.inspection_date = action.payload.inspection_date;
      state.inspection_region = action.payload.inspection_region;
      state.inspection_address = action.payload.inspection_address;
    },

    addPhotoInspection: (
      state: Draft<ISosState>,
      action: PayloadAction<{
        inspection_region: string;
        inspection_address: string;
      }>
    ) => {
      state.inspection_region = action.payload.inspection_region;
      state.inspection_address = action.payload.inspection_address;
    },

    addInspectionTab: (
      state: Draft<ISosState>,
      action: PayloadAction<0 | 1>
    ) => {
      state.inspectionTab = action.payload;
    },

    setVictim: (state: Draft<ISosState>, action: PayloadAction<boolean>) => {
      state.isVictim = action.payload;
    },

    addEvaluationReportNumber: (
      state: Draft<ISosState>,
      action: PayloadAction<string>
    ) => {
      state.evaluation_report_number = action.payload;
    },

    addOtpCode: (state: Draft<ISosState>, action: PayloadAction<string>) => {
      state.otp_code = action.payload;
    },

    resetOtpCode: (state: Draft<ISosState>) => {
      state.otp_code = '';
    },

    addRefId: (state: Draft<ISosState>, action: PayloadAction<string>) => {
      state.refId = action.payload;
    },

    resetRefId: (state: Draft<ISosState>) => {
      state.refId = '';
    },

    addAssessmentOtpCode: (
      state: Draft<ISosState>,
      action: PayloadAction<string>
    ) => {
      state.assessment_otp_code = action.payload;
    },

    resetAssessmentOtpCode: (state: Draft<ISosState>) => {
      state.assessment_otp_code = '';
    },

    addAssessmentRefId: (
      state: Draft<ISosState>,
      action: PayloadAction<string>
    ) => {
      state.assessment_refId = action.payload;
    },

    resetAssessmentRefId: (state: Draft<ISosState>) => {
      state.assessment_refId = '';
    },
  },
});

export const {
  reset,
  addDocNumber,
  addPhoto,
  addCar,
  resetPhoto,
  addPhotoTemp,
  addLocation,
  addDate,
  addInfo,
  addDocuments,
  resetDocuments,
  addDocumentsTemp,
  addInjured,
  addInspection,
  addPhotoInspection,
  addInspectionTab,
  setVictim,
  addEvaluationReportNumber,
  addVehicles,
  addOtpCode,
  addRefId,
  resetOtpCode,
  resetRefId,
  addAssessmentOtpCode,
  resetAssessmentOtpCode,
  addAssessmentRefId,
  resetAssessmentRefId,
} = sosSlice.actions;
export default sosSlice.reducer;
